import * as XLSX from 'xlsx';
import { normalizeDate } from '../../../pages/schedules-templates/utils/excelParser';

export interface ParsedLessonPlanRow {
  chapter: string;
  section: string;
  sectionCode: string | null;
  startDate: string | null;
  endDate: string | null;
  resourceName: string | null;
  resourceType: string | null;
  resourceUrl: string | null;
  _rawStartDate?: any; // Original Excel value for error reporting
  _rawEndDate?: any;   // Original Excel value for error reporting
}

function cellString(value: any): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value).trim() || null;
}

function getCellValue(sheet: XLSX.WorkSheet, row: number, col: number): any {
  const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
  return cell ? cell.v : null;
}

function findCol(colMap: Record<string, number>, ...names: string[]): number | undefined {
  for (const name of names) {
    if (colMap[name] !== undefined) return colMap[name];
  }
  return undefined;
}

/**
 * Find a header row and map column indices.
 * Looks for a row containing "chapter" or "section" in the first 5 rows.
 */
function mapHeaders(sheet: XLSX.WorkSheet): { headerRow: number; colMap: Record<string, number> } | null {
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');

  for (let r = range.s.r; r <= Math.min(range.s.r + 5, range.e.r); r++) {
    const colMap: Record<string, number> = {};
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.v) {
        const header = String(cell.v).trim().toLowerCase();
        colMap[header] = c;
      }
    }
    if (colMap['chapter'] !== undefined || colMap['section'] !== undefined) {
      return { headerRow: r, colMap };
    }
  }
  return null;
}

/**
 * Parse an ArrayBuffer into an XLSX workbook.
 */
export function parseExcelFile(buffer: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(buffer, { type: 'array', cellDates: true });
}

/**
 * Parse the first sheet of a workbook as lesson plan rows.
 * Supports carry-forward for Chapter and Section columns (handles merged cells).
 */
export function parseLessonPlanSheet(workbook: XLSX.WorkBook): ParsedLessonPlanRow[] {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];

  const headerInfo = mapHeaders(sheet);
  if (!headerInfo) return [];

  const { headerRow, colMap } = headerInfo;
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const rows: ParsedLessonPlanRow[] = [];

  const chapterCol = findCol(colMap, 'chapter', 'group', 'chapter name');
  const sectionCol = findCol(colMap, 'section', 'step', 'section name');
  const sectionCodeCol = findCol(colMap, 'section code', 'sectioncode', 'code');
  const startDateCol = findCol(colMap, 'start date', 'startdate', 'start');
  const endDateCol = findCol(colMap, 'end date', 'enddate', 'end');
  const resourceNameCol = findCol(colMap, 'resource name', 'resourcename', 'resource');
  const resourceTypeCol = findCol(colMap, 'resource type', 'resourcetype', 'type');
  const resourceUrlCol = findCol(colMap, 'resource url', 'resourceurl', 'url');

  // Carry-forward state
  let lastChapter = '';
  let lastSection = '';
  let lastSectionCode: string | null = null;
  let lastStartDate: string | null = null;
  let lastEndDate: string | null = null;

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const rawChapter = chapterCol !== undefined ? cellString(getCellValue(sheet, r, chapterCol)) : null;
    const rawSection = sectionCol !== undefined ? cellString(getCellValue(sheet, r, sectionCol)) : null;
    const rawSectionCode = sectionCodeCol !== undefined ? cellString(getCellValue(sheet, r, sectionCodeCol)) : null;
    const rawStartDate = startDateCol !== undefined ? getCellValue(sheet, r, startDateCol) : null;
    const rawEndDate = endDateCol !== undefined ? getCellValue(sheet, r, endDateCol) : null;
    const resourceName = resourceNameCol !== undefined ? cellString(getCellValue(sheet, r, resourceNameCol)) : null;
    const resourceType = resourceTypeCol !== undefined ? cellString(getCellValue(sheet, r, resourceTypeCol)) : null;
    const resourceUrl = resourceUrlCol !== undefined ? cellString(getCellValue(sheet, r, resourceUrlCol)) : null;

    // Skip completely empty rows
    if (!rawChapter && !rawSection && !rawSectionCode && !rawStartDate && !rawEndDate && !resourceName && !resourceType && !resourceUrl) {
      continue;
    }

    // Apply carry-forward logic
    let chapter: string;
    let section: string;
    let sectionCode: string | null;
    let startDate: string | null;
    let endDate: string | null;

    if (rawChapter) {
      // New chapter — reset section carry-forward
      chapter = rawChapter;
      section = rawSection || '';
      sectionCode = rawSectionCode;
      startDate = normalizeDate(rawStartDate);
      endDate = normalizeDate(rawEndDate);
      lastChapter = chapter;
      lastSection = section;
      lastSectionCode = sectionCode;
      lastStartDate = startDate;
      lastEndDate = endDate;
    } else if (rawSection) {
      // Same chapter, new section
      chapter = lastChapter;
      section = rawSection;
      sectionCode = rawSectionCode;
      startDate = normalizeDate(rawStartDate);
      endDate = normalizeDate(rawEndDate);
      lastSection = section;
      lastSectionCode = sectionCode;
      lastStartDate = startDate;
      lastEndDate = endDate;
    } else {
      // Same chapter and section (additional resource row)
      chapter = lastChapter;
      section = lastSection;
      sectionCode = lastSectionCode;
      startDate = lastStartDate;
      endDate = lastEndDate;
    }

    rows.push({
      chapter,
      section,
      sectionCode,
      startDate,
      endDate,
      resourceName,
      resourceType,
      resourceUrl,
      _rawStartDate: rawStartDate,
      _rawEndDate: rawEndDate,
    });
  }

  return rows;
}
